import { useState } from "react";
import { useTranslation } from "react-i18next";

const planColors = ["#a0845c", "#c9a84c", "#d4af37"];
const planIcons = ["ri-medal-line", "ri-medal-2-line", "ri-vip-crown-fill"];
const planIds = ["starter", "standard", "premium"];
const planInvestments = [3000, 5000, 10000];
const planProfits = [30, 40, 50];

export default function Contracts() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState("standard");

  const plans = [
    {
      id: planIds[0],
      name: t("contracts.plan1.name"),
      investment: planInvestments[0],
      profit: planProfits[0],
      badge: null,
      color: planColors[0],
      icon: planIcons[0],
      features: [
        t("contracts.plan1.f1"), t("contracts.plan1.f2"), t("contracts.plan1.f3"),
        t("contracts.plan1.f4"), t("contracts.plan1.f5"),
      ],
    },
    {
      id: planIds[1],
      name: t("contracts.plan2.name"),
      investment: planInvestments[1],
      profit: planProfits[1],
      badge: t("contracts.plan2.badge"),
      color: planColors[1],
      icon: planIcons[1],
      features: [
        t("contracts.plan2.f1"), t("contracts.plan2.f2"), t("contracts.plan2.f3"),
        t("contracts.plan2.f4"), t("contracts.plan2.f5"), t("contracts.plan2.f6"),
      ],
    },
    {
      id: planIds[2],
      name: t("contracts.plan3.name"),
      investment: planInvestments[2],
      profit: planProfits[2],
      badge: t("contracts.plan3.badge"),
      color: planColors[2],
      icon: planIcons[2],
      features: [
        t("contracts.plan3.f1"), t("contracts.plan3.f2"), t("contracts.plan3.f3"),
        t("contracts.plan3.f4"), t("contracts.plan3.f5"), t("contracts.plan3.f6"), t("contracts.plan3.f7"),
      ],
    },
  ];

  return (
    <section id="contracts" className="bg-[#0d0d0d] py-24 px-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#c9a84c]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-xs font-bold tracking-[4px] uppercase">{t("contracts.badge")}</span>
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {t("contracts.title1")} <span className="text-[#c9a84c]">{t("contracts.title2")}</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto text-base">{t("contracts.desc")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan) => {
            const isSelected = selected === plan.id;
            const roi = Math.round(plan.investment * (plan.profit / 100));
            const total = plan.investment + roi;

            return (
              <div
                key={plan.id}
                onClick={() => setSelected(plan.id)}
                className={`relative rounded-2xl p-7 cursor-pointer transition-all duration-300 ${
                  isSelected
                    ? "bg-gradient-to-b from-[#1a1500] to-[#0b0b0b] border-2"
                    : "bg-white/5 border border-white/10 hover:border-white/20"
                }`}
                style={isSelected ? { borderColor: plan.color } : {}}
              >
                {plan.badge && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-black px-4 py-1 rounded-full uppercase tracking-wider text-[#0b0b0b] whitespace-nowrap"
                    style={{ backgroundColor: plan.color }}
                  >
                    {plan.badge}
                  </div>
                )}

                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${plan.color}20` }}>
                    <i className={`${plan.icon} text-2xl`} style={{ color: plan.color }} />
                  </div>
                  <div>
                    <h3 className="text-white font-black text-lg">{plan.name}</h3>
                    <p className="text-white/40 text-xs">{t("contracts.subLabel")}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-5xl font-black" style={{ color: plan.color }}>${plan.investment.toLocaleString()}</span>
                  </div>
                  <p className="text-white/40 text-xs">{t("contracts.minInvestment")}</p>
                </div>

                <div className="bg-white/5 rounded-xl p-4 mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-white/50 text-xs">{t("contracts.profitRate")}</span>
                    <span className="font-black text-sm" style={{ color: plan.color }}>{plan.profit}%</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-white/50 text-xs">{t("contracts.estReturn")}</span>
                    <span className="text-white font-bold text-sm">+${roi.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 mt-2 flex justify-between">
                    <span className="text-white/50 text-xs">{t("contracts.totalPayout")}</span>
                    <span className="text-[#c9a84c] font-black text-sm">${total.toLocaleString()}</span>
                  </div>
                </div>

                <ul className="space-y-2 mb-7">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-white/70 text-xs">
                      <i className="ri-checkbox-circle-fill mt-0.5 shrink-0" style={{ color: plan.color }} />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="w-full py-3 rounded-xl font-black text-sm transition-all cursor-pointer whitespace-nowrap"
                  style={
                    isSelected
                      ? { backgroundColor: plan.color, color: "#0b0b0b" }
                      : { backgroundColor: "transparent", border: `1px solid ${plan.color}40`, color: plan.color }
                  }
                >
                  {t("contracts.getStarted")}
                </button>
              </div>
            );
          })}
        </div>

        <div className="bg-[#c9a84c]/10 border border-[#c9a84c]/30 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center shrink-0">
            <i className="ri-shield-star-fill text-[#c9a84c] text-3xl" />
          </div>
          <p className="text-white/80 text-sm text-center md:text-left leading-relaxed">
            <strong className="text-[#c9a84c]">{t("contracts.guaranteeTitle")}</strong>{" "}
            {t("contracts.guaranteeText")}
          </p>
        </div>
      </div>
    </section>
  );
}
